import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api/client';
import '../backlog/backlog.css';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { FormTitle } from '../../components/FormTitle';
import { useConfirm } from '../../components/confirm';
import { useNotify } from '../../components/notifications/useNotify';
import { runLoading } from '../../components/loading';
import { AppToolbar } from '../../components/AppToolbar';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { StepHint } from '../../components/StepHint/StepHint';
import { fmtLocalDateTime } from '../../utils/datetime';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────
interface ApprovalListRow {
  jobId:             string;
  jobOid:            string;
  client:            string;
  projectId:         string;
  projectName:       string;
  sampleMatrix:      string;
  status:            string;
  statusId:          string;
  samplingStatusInt: number;
}

interface StationRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  blended:        boolean;
  latitude:       string | null;
  longitude:      string | null;
  temperature:    string | null;
  humidity:       string | null;
  stationComment: string | null;
}

interface SamplingRow {
  sampleLoginOid: string;
  stationId:      string;
  stationName:    string;
  sampleType:     string;
  sampleId:       string;
  collectorOid:   string | null;
  collectorName:  string | null;
  collectedDate:  string | null;
  sampleComment:  string;
  testSummary:    string;
  enteredBy:      string;
  enteredDate:    string | null;
}

interface MonitoringRow {
  sampleParameterOid: string;
  sampleLoginOid:     string;
  sampleId:           string;
  test:               string;
  methodName:         string;
  parameter:          string;
  result:             string;
  unit:               string;
  monitoredBy:        string;
  monitoredDate:      string | null;
  enteredBy:          string;
  enteredDate:        string | null;
}

type TabKey = 'stations' | 'sampling' | 'monitoring';

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function FieldDataApprovalPage() {
  // ── List state ──────────────────────────────────────────────────────────────
  const [listRows,    setListRows]    = useState<ApprovalListRow[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [listLoadMs,  setListLoadMs]  = useState<number | null>(null);
  const [listError,   setListError]   = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());

  // ── Detail state ────────────────────────────────────────────────────────────
  const [view,          setView]          = useState<'list' | 'detail'>('list');
  const [selectedJob,   setSelectedJob]   = useState<ApprovalListRow | null>(null);
  const [activeTab,     setActiveTab]     = useState<TabKey>('stations');
  const [visitedTabs,   setVisitedTabs]   = useState<Set<TabKey>>(new Set());
  const [stations,      setStations]      = useState<StationRow[]>([]);
  const [sampling,      setSampling]      = useState<SamplingRow[]>([]);
  const [monitoring,    setMonitoring]    = useState<MonitoringRow[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailLoadMs,  setDetailLoadMs]  = useState<number | null>(null);
  // Permission gate ported from GoLive FieldDataReviewController.OnActivated —
  // Approve/Rollback are enabled only for admins or roles with Write=true on
  // the FieldDataReview2 navigation permission.
  const [canWrite, setCanWrite] = useState(true);

  const notify                              = useNotify();
  const { confirm: confirmDlg, prompt: promptDlg } = useConfirm();
  const detailT0 = useRef<number>(0);

  // ── Load list ──────────────────────────────────────────────────────────────
  const loadList = useCallback(async (searchText?: string) => {
    setListLoading(true);
    setListError(null);
    const t0 = performance.now();
    try {
      const params: Record<string, string> = {};
      if (searchText) params.search = searchText;
      const res = await api.get('/api/v1/field-data-approval', { params });
      setListRows((res.data as { data: ApprovalListRow[] }).data ?? []);
      setSelectedIds(new Set());
    } catch (e: unknown) {
      setListError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setListLoadMs(performance.now() - t0);
      setListLoading(false);
    }
  }, []);

  useEffect(() => { void loadList(); }, [loadList]);

  // ── Single-select (radio-button style) — only one job may be ticked at a
  // time, since Load/Rollback operate on one record here. Header "select all"
  // collapses to the last row rather than checking every row.
  const handleSelectionChange = useCallback((next: Set<string | number>) => {
    if (next.size <= 1) { setSelectedIds(next); return; }
    const newlyAdded = [...next].find(id => !selectedIds.has(id));
    setSelectedIds(new Set([newlyAdded ?? [...next][next.size - 1]!]));
  }, [selectedIds]);

  // ── Permission gate (ported from GoLive FieldDataReviewController.OnActivated) ──
  useEffect(() => {
    api.get('/api/v1/field-data-approval/permissions')
      .then(res => setCanWrite(Boolean((res.data as { data: { canWrite: boolean } }).data?.canWrite)))
      .catch(() => setCanWrite(false));
  }, []);

  // ── Load detail (after clicking Load) ──────────────────────────────────────
  const openDetail = useCallback(async (job: ApprovalListRow) => {
    setSelectedJob(job);
    setDetailLoading(true);
    setActiveTab('stations');
    setVisitedTabs(new Set(['stations']));
    detailT0.current = performance.now();
    try {
      const [st, sp, mn] = await Promise.all([
        api.get(`/api/v1/field-data-approval/${job.jobOid}/stations`),
        api.get(`/api/v1/field-data-approval/${job.jobOid}/sampling`),
        api.get(`/api/v1/field-data-approval/${job.jobOid}/monitoring`),
      ]);
      setStations((st.data as { data: StationRow[] }).data ?? []);
      setSampling((sp.data as { data: SamplingRow[] }).data ?? []);
      setMonitoring((mn.data as { data: MonitoringRow[] }).data ?? []);
      setDetailLoadMs(performance.now() - detailT0.current);
      setView('detail');
    } catch {
      notify.error('Failed to load job detail. Please try again.');
    } finally {
      setDetailLoading(false);
    }
  }, [notify]);

  const closeDetail = useCallback(() => {
    setView('list');
    setSelectedJob(null);
    setStations([]); setSampling([]); setMonitoring([]);
    setVisitedTabs(new Set());
    void loadList();
  }, [loadList]);

  // ── Tab navigation — marks tab as visited ───────────────────────────────────
  const switchTab = useCallback((tab: TabKey) => {
    setActiveTab(tab);
    setVisitedTabs(prev => new Set([...prev, tab]));
  }, []);

  const allTabsVisited = visitedTabs.has('stations') && visitedTabs.has('sampling') && visitedTabs.has('monitoring');

  // ── Approve ─────────────────────────────────────────────────────────────────
  // AI approves the whole job at once (no per-sample checkbox selection —
  // that's a GoLive-only UX choice).
  const handleApprove = useCallback(async () => {
    if (!selectedJob) return;
    // No "all three tabs visited" gate (removed 2026-08-03, same as Field Data
    // Validation): GoLive FieldDataApproved_Execute gates ONLY on the Sampling
    // selection + the FieldDataReview2 Write permission. Requiring a tab sweep
    // blocked users who legitimately held Write via Module Access Control. The tab
    // ticks stay as progress indicators, not a gate.
    if (!canWrite) {
      notify.error('You do not have permission to approve field data.');
      return;
    }
    const ok = await confirmDlg({
      message: `Approve job ${selectedJob.jobId}? This will mark it as Completed (FD-2-5).`,
      tone: 'default',
    });
    if (!ok) return;
    try {
      await runLoading(
        api.post(`/api/v1/field-data-approval/${selectedJob.jobOid}/approve`, {}),
        { blocking: true, label: 'Approving…' }
      );
      notify.success('Approve completed successfully.');
      closeDetail();
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string } | undefined)?.message ?? 'Approve failed.';
      notify.error(msg);
    }
  }, [selectedJob, allTabsVisited, canWrite, confirmDlg, notify, closeDetail]);

  // ── Rollback (detail view — scoped to every sample shown in the Sampling
  // Information tab, matches GoLive ApprovedRollBack_Accepting exactly: no
  // per-row pick needed, the tab is already the set of PendingApproval rows
  // for this job) ──
  const handleRollback = useCallback(async () => {
    if (!selectedJob) return;
    if (sampling.length === 0) {
      notify.error('No samples to rollback.');
      return;
    }
    const count = sampling.length;
    const reason = await promptDlg({
      title: 'Rollback Reason',
      message: `Enter reason for rolling back ${count} sample${count > 1 ? 's' : ''} in job ${selectedJob.jobId}:`,
      required: true,
    });
    if (!reason) return;
    try {
      await runLoading(
        api.post(`/api/v1/field-data-approval/${selectedJob.jobOid}/rollback`, {
          reason, selectedSampleOids: sampling.map(r => r.sampleLoginOid),
        }),
        { blocking: true, label: 'Rolling back…' }
      );
      notify.success('Rolled back successfully.');
      closeDetail();
    } catch (err: unknown) {
      const raw = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const msg = typeof raw === 'string' ? raw : (raw as { message?: string } | undefined)?.message ?? 'Rollback failed.';
      notify.error(msg);
    }
  }, [selectedJob, sampling, promptDlg, notify, closeDetail]);

  // ── Island stepper ──────────────────────────────────────────────────────────
  const islandSteps = useMemo((): IslandStep[] => {
    if (view === 'list') {
      const jobDone = selectedIds.size > 0;
      return [
        { id: 'job',        label: 'Select Job', state: jobDone ? 'done'   : 'active' },
        { id: 'stations',   label: 'Stations',   state: 'todo' },
        { id: 'sampling',   label: 'Sampling',   state: 'todo' },
        { id: 'monitoring', label: 'Monitoring', state: 'todo' },
        { id: 'approve',    label: 'Approve',    state: 'todo' },
      ];
    }
    return [
      { id: 'job',        label: 'Select Job', state: 'done' },
      { id: 'stations',   label: 'Stations',   state: visitedTabs.has('stations')   ? 'done' : 'active' },
      { id: 'sampling',   label: 'Sampling',   state: visitedTabs.has('sampling')   ? 'done' : visitedTabs.has('stations')   ? 'active' : 'todo' },
      { id: 'monitoring', label: 'Monitoring', state: visitedTabs.has('monitoring') ? 'done' : visitedTabs.has('sampling')   ? 'active' : 'todo' },
      { id: 'approve',    label: 'Approve',    state: allTabsVisited ? 'active' : 'todo' },
    ];
  }, [view, selectedIds, visitedTabs, allTabsVisited]);

  // ── List grid columns ───────────────────────────────────────────────────────
  const listCols: AppColumn<ApprovalListRow>[] = [
    { field: 'jobId',        header: 'Job ID',       width: 130 },
    { field: 'client',       header: 'Client',       width: 220 },
    { field: 'projectId',    header: 'Project ID',   width: 130 },
    { field: 'projectName',  header: 'Project Name', width: 230 },
    { field: 'sampleMatrix', header: 'Sample Matrix',width: 140 },
    { field: 'status',       header: 'Status',       width: 210,
      render: r => <span className="bl-pill ok">{r.status}</span> },
    { field: 'statusId',     header: 'Status ID',    width: 100 },
  ];

  // ── Station grid columns (read-only) ────────────────────────────────────────
  const stationCols: AppColumn<StationRow>[] = [
    { field: 'stationId',      header: 'JobID',             width: 120 },
    { field: 'stationName',    header: 'StationLocation',   width: 160 },
    { field: 'blended',        header: 'Blended',           width: 90, align: 'center',
      render: r => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <input type="checkbox" checked={r.blended} readOnly
            style={{ cursor: 'default', width: 14, height: 14 }} />
        </div>
      ) },
    { field: 'latitude',       header: 'Latitude',          width: 120 },
    { field: 'longitude',      header: 'Longitude',         width: 120 },
    { field: 'temperature',    header: 'Temperature (°F)',  width: 140 },
    { field: 'humidity',       header: 'Humidity (%)',      width: 120 },
    { field: 'stationComment', header: 'StationComment',    width: 200 },
  ];

  // ── Sampling grid columns (read-only) ───────────────────────────────────────
  const samplingCols: AppColumn<SamplingRow>[] = [
    { field: 'stationName',     header: 'StationLocation',  width: 150 },
    { field: 'sampleId',        header: 'SampleID',         width: 140 },
    { field: 'collectorName',   header: 'CollectedBy',      width: 140 },
    { field: 'collectedDate',   header: 'CollectedDate',    width: 160, type: 'datetime',
      render: r => <span>{r.collectedDate ? fmtLocalDateTime(r.collectedDate) : ''}</span> },
    { field: 'testSummary',     header: 'TestSummary',      width: 340 },
    { field: 'sampleComment',   header: 'SampleComment',    width: 180 },
    { field: 'enteredBy',       header: 'EnteredBy',        width: 120 },
    { field: 'enteredDate',     header: 'EnteredDate',      width: 160, type: 'datetime',
      render: r => <span>{r.enteredDate ? fmtLocalDateTime(r.enteredDate) : ''}</span> },
  ];

  // ── Monitoring grid columns (read-only) ─────────────────────────────────────
  const monitoringCols: AppColumn<MonitoringRow>[] = [
    { field: 'sampleId',    header: 'SampleID',       width: 140 },
    { field: 'test',        header: 'Test',           width: 180 },
    { field: 'methodName',  header: 'MethodName',     width: 140 },
    { field: 'parameter',   header: 'Parameter',      width: 180 },
    { field: 'result',      header: 'Result',         width: 90, align: 'right' },
    { field: 'unit',        header: 'Units',          width: 110 },
    { field: 'monitoredBy', header: 'MonitoredBy',    width: 130 },
    { field: 'monitoredDate', header: 'MonitoredDate', width: 160, type: 'datetime',
      render: r => <span>{r.monitoredDate ? fmtLocalDateTime(r.monitoredDate) : ''}</span> },
    { field: 'enteredBy',   header: 'EnteredBy',      width: 120 },
    { field: 'enteredDate', header: 'EnteredDate',    width: 160, type: 'datetime',
      render: r => <span>{r.enteredDate ? fmtLocalDateTime(r.enteredDate) : ''}</span> },
  ];

  const loadedJob = selectedIds.size === 1
    ? listRows.find(r => selectedIds.has(r.jobOid)) ?? null
    : null;

  // ═══════════════════════════════════════════════════════════════════
  // LIST VIEW
  // ═══════════════════════════════════════════════════════════════════
  if (view === 'list') {
    return (
      <div className="bl-page">
        <TopbarStepper steps={islandSteps} />

        <div className="bl-top">
          <div className="dp-page-hdr">
            <div className="dp-breadcrumb">
              <span>Home</span><i className="ti ti-chevron-right" />
              <span>Sampling Management</span><i className="ti ti-chevron-right" />
              <span className="dp-bc-active">Field Data Approval</span>
            </div>

            <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <FormTitle pageId="fielddata-approval" auditFormName="Field Data Approval"
                loadMs={listLoadMs} loading={listLoading} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  className="ca-btn ca-btn-solid"
                  disabled={!loadedJob}
                  onClick={() => { if (loadedJob) void openDetail(loadedJob); }}
                  title={!loadedJob ? 'Tick a Job ID to load' : `Load ${loadedJob.jobId}`}
                >
                  <span aria-hidden="true">⬇️</span>&nbsp;Load
                </button>
                <button
                  className="ca-btn"
                  disabled={!loadedJob || !canWrite}
                  title={!canWrite ? 'You do not have permission to roll back field data' : undefined}
                  onClick={async () => {
                    if (!loadedJob) return;
                    const reason = await promptDlg({
                      title: 'Rollback Reason',
                      message: `Enter reason for rolling back job ${loadedJob.jobId}:`,
                      required: true,
                    });
                    if (!reason) return;
                    try {
                      await runLoading(
                        api.post(`/api/v1/field-data-approval/${loadedJob.jobOid}/rollback`, { reason }),
                        { blocking: true, label: 'Rolling back…' }
                      );
                      notify.success('Rolled back to Pending Submission (data intact).');
                      void loadList();
                    } catch {
                      notify.error('Rollback failed.');
                    }
                  }}
                >
                  <span aria-hidden="true">↩️</span>&nbsp;Rollback
                </button>
              </div>
            </div>

            <div className="dp-subtitle">
              Validated field data awaiting final approval (FD-2-4). Tick a Job ID and click <strong>Load</strong> to review.
            </div>

            <StepHint tone="info">
              <b>Review · Approval.</b> Open a validated job, review the three tabs, then <b>Approve</b> — or <b>Rollback</b> to return it to Pending Submission (data intact).
            </StepHint>
          </div>
        </div>

        <div className="bl-grid-area">
          <div style={{ margin: '0 0 8px' }}>
            <AppToolbar
              onRefresh={() => void loadList()}
              refreshing={listLoading}
              onExport={() => notify.info('Export started.')}
            />
          </div>

          <div className="bl-card bl-card-flex">
            {listError ? (
              <div className="bl-err" style={{ margin: 12 }}>
                <i className="ti ti-alert-circle" /> {listError}
                <button onClick={() => void loadList()}
                  style={{ marginLeft: 10, color: 'var(--brand-accent,#e07b39)', border: 'none', background: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                  Retry
                </button>
              </div>
            ) : (
              <AppDataGrid<ApprovalListRow>
                rows={listRows}
                columns={listCols}
                getRowId={r => r.jobOid}
                loading={listLoading}
                selectable
                selectedIds={selectedIds}
                onSelectionChange={handleSelectionChange}
                emptyText="Nothing pending approval — validate a job in Field Data Validation."
                onRowDoubleClick={r => void openDetail(r)}
              />
            )}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ═══════════════════════════════════════════════════════════════════
  const hintContent = allTabsVisited
    ? (<>All three tabs reviewed — click <strong>Approve</strong> to move this job forward, or <strong>Rollback</strong> to send it back.</>)
    : (<><b>Review · Approval.</b> Read-only review of the validated field data — visit all three tabs, then <strong>Approve</strong>. Rollback returns the job to Pending Submission.</>);

  return (
    <div className="bl-page">
      <TopbarStepper steps={islandSteps} />

      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span
              style={{ color: 'var(--brand-accent,#e07b39)', cursor: 'pointer', textDecoration: 'underline' }}
              onClick={closeDetail}
            >Field Data Approval</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{selectedJob?.jobId}</span>
          </div>

          <div className="dp-title-row" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormTitle
              pageId="fielddata-approval"
              title={`Field Data Approval — ${selectedJob?.jobId ?? ''}`}
              auditFormName="Field Data Approval"
              loadMs={detailLoadMs}
              loading={detailLoading}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                className="ca-btn ca-btn-solid"
                /* Enabled purely by the MAC Write permission (GoLive parity) — the
                   tab-visit rule was removed 2026-08-03, see handleApprove. */
                disabled={!canWrite}
                title={!canWrite ? 'You do not have permission to approve field data' : 'Approve this job'}
                onClick={() => void handleApprove()}
              >
                <span aria-hidden="true">✓</span>&nbsp;Approve
              </button>
              <button
                className="ca-btn"
                disabled={!canWrite || sampling.length === 0}
                title={!canWrite ? 'You do not have permission to roll back field data' : sampling.length === 0 ? 'No samples to rollback' : `Roll back ${sampling.length} sample(s)`}
                onClick={() => void handleRollback()}
              >
                <span aria-hidden="true">↩️</span>&nbsp;Rollback
              </button>
            </div>
          </div>

          <StepHint tone={allTabsVisited ? 'complete' : 'info'}>
            {hintContent}
          </StepHint>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="bl-grid-area">
        <div className="sa-filter-row" style={{ marginBottom: 8 }}>
          {(['stations', 'sampling', 'monitoring'] as TabKey[]).map(tab => {
            const labels: Record<TabKey, string> = {
              stations:   'Station Information',
              sampling:   'Sampling Information',
              monitoring: 'Monitoring Records',
            };
            const visited = visitedTabs.has(tab);
            return (
              <button
                key={tab}
                className={`sa-filter-btn${activeTab === tab ? ' active' : ''}`}
                onClick={() => switchTab(tab)}
              >
                {labels[tab]}
                {visited && <span style={{ color: '#22c55e', marginLeft: 5, fontWeight: 700 }}>✓</span>}
              </button>
            );
          })}
        </div>

        {/* Read-only notice + toolbar */}
        <div style={{ margin: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <AppToolbar
            onRefresh={() => {
              if (selectedJob) void openDetail(selectedJob);
            }}
            refreshing={detailLoading}
            onExport={() => notify.info('Export started.')}
          />
          <span style={{ fontSize: 11, color: 'var(--ai-muted)', marginLeft: 4 }}>Read-only review.</span>
        </div>

        {/* ── Station Information tab ── */}
        {activeTab === 'stations' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<StationRow>
              rows={stations}
              columns={stationCols}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No station rows."
            />
          </div>
        )}

        {/* ── Sampling Information tab (read-only) — Rollback acts on every row
               shown here (all PendingApproval samples for this job), matching
               GoLive ApprovedRollBack_Accepting exactly, with no pick step needed ── */}
        {activeTab === 'sampling' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<SamplingRow>
              rows={sampling}
              columns={samplingCols}
              getRowId={r => r.sampleLoginOid}
              loading={detailLoading}
              emptyText="No sampling rows."
            />
          </div>
        )}

        {/* ── Monitoring Records tab ── */}
        {activeTab === 'monitoring' && (
          <div className="bl-card bl-card-flex">
            <AppDataGrid<MonitoringRow>
              rows={monitoring}
              columns={monitoringCols}
              getRowId={r => r.sampleParameterOid}
              loading={detailLoading}
              emptyText="No monitoring rows."
            />
          </div>
        )}
      </div>
    </div>
  );
}
